import { useCallback, useEffect, useRef, type RefObject } from 'react';

export const BOTTOM_THRESHOLD_PX = 80;

export function isNearScrollBottom(el: HTMLElement, thresholdPx = BOTTOM_THRESHOLD_PX): boolean {
  const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
  return distance <= thresholdPx;
}

export function scrollToBottomIfNearEnd(
  scrollRef: RefObject<HTMLElement | null> | null | undefined,
  thresholdPx = BOTTOM_THRESHOLD_PX,
): void {
  const el = scrollRef?.current;
  if (!el || !isNearScrollBottom(el, thresholdPx)) {
    return;
  }
  el.scrollTop = el.scrollHeight;
}

type UseStickToBottomOptions = {
  scrollRef: RefObject<HTMLElement | null> | null | undefined;
  deps: unknown[];
  forceScroll?: boolean;
};

export function useStickToBottom({
  scrollRef,
  deps,
  forceScroll = false,
}: UseStickToBottomOptions) {
  const userScrolledUpRef = useRef(false);

  const isNearBottom = useCallback(
    (el: HTMLElement) => isNearScrollBottom(el, BOTTOM_THRESHOLD_PX),
    [],
  );

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;

    const onScroll = () => {
      userScrolledUpRef.current = !isNearBottom(el);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef, isNearBottom]);

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;

    const shouldScroll = forceScroll || !userScrolledUpRef.current;
    if (!shouldScroll) return undefined;

    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToBottom);
    });

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller passes intentional deps
  }, deps);
}
